<template>
  <section class="page">
    <div class="page__header">
      <div class="page__title">
        <SharedBreadcrumbs :breadcrumbs />

        <FaqSearch />
      </div>

      <h1 class="typography__headline1">Добавление&nbsp;и&nbsp;редактирование заявки</h1>
    </div>

    <div class="page__content">
      <FaqArticlesAside :menu />

      <FaqArticlesContent>
        <div class="base-content-block">
          <FaqArticlesBlockHeading
            id="kak-popast"
            class="typography__headline2"
          >
            Как попасть
          </FaqArticlesBlockHeading>

          <div class="block-small">
            <p>Давайте добавим заявку в&nbsp;ваш проект.</p>

            <p>
              В&nbsp;меню слева перейдите в&nbsp;раздел &laquo;Заявки&raquo;. Если у&nbsp;вас еще нет
              заявок&nbsp;&mdash; на&nbsp;появившейся странице нажмите &laquo;Добавить заявку&raquo;.
            </p>
          </div>

          <FaqArticlesImageModalButton
            image-source="/images/faq/dobavleniye-i-redaktirovaniye-zayavki-1.jpg"
            @click="(imageSource) => (activeImageSource = imageSource)"
          />

          <p>Если заявки уже есть&nbsp;&mdash; на&nbsp;появившейся странице нажмите &laquo;Добавить заявку&raquo;.</p>

          <FaqArticlesImageModalButton
            image-source="/images/faq/dobavleniye-i-redaktirovaniye-zayavki-2.jpg"
            @click="(imageSource) => (activeImageSource = imageSource)"
          />
        </div>

        <div class="base-content-block">
          <FaqArticlesBlockHeading
            id="dobavleniye-zayavki"
            class="typography__headline2"
          >
            Добавление заявки
          </FaqArticlesBlockHeading>

          <div class="block-small">
            <p>
              В&nbsp;основной информации по-умолчанию выставлен статус &laquo;Новая&raquo;. Заявка с&nbsp;таким статусом
              будет отображаться с&nbsp;уведомлением в&nbsp;меню.
            </p>

            <p>Ниже выберите нужных клиента и&nbsp;услугу для этой заявки.</p>
          </div>

          <FaqArticlesImageModalButton
            image-source="/images/faq/dobavleniye-i-redaktirovaniye-zayavki-3.jpg"
            @click="(imageSource) => (activeImageSource = imageSource)"
          />

          <p>
            Как заполните все нужные данные заявки, нажмите &laquo;Добавить&raquo; в&nbsp;самом низу страницы. После
            успешного добавления увидите в&nbsp;правом верхнем углу уведомление.
          </p>

          <FaqArticlesImageModalButton
            image-source="/images/faq/dobavleniye-i-redaktirovaniye-zayavki-4.jpg"
            @click="(imageSource) => (activeImageSource = imageSource)"
          />
        </div>

        <div class="base-content-block">
          <FaqArticlesBlockHeading
            id="redaktirovaniye-zayavki"
            class="typography__headline2"
          >
            Редактирование заявки
          </FaqArticlesBlockHeading>

          <div class="block-small">
            <p>После добавления заявки, вы&nbsp;сможете найти её&nbsp;в&nbsp; <BaseLink
              target="_blank"
              to="/faq/articles/spisok-zayavok-i-ikh-dannye"
            >списке заявок</BaseLink>.</p>

            <p>Отсюда можно быстро перейти к&nbsp;редактированию по&nbsp;иконке карандаша справа.</p>
          </div>

          <FaqArticlesImageModalButton
            image-source="/images/faq/dobavleniye-i-redaktirovaniye-zayavki-5.jpg"
            @click="(imageSource) => (activeImageSource = imageSource)"
          />

          <p>
            С&nbsp;добавлением заявки разобрались. Самое время перейти к&nbsp;изучению
            <BaseLink
              class="ui-link-secondary"
              to="/faq/articles/dobavleniye-i-redaktirovaniye-zayavki"
              >списка заявок и&nbsp;просмотра их&nbsp;данных </BaseLink
            >.
          </p>
        </div>
      </FaqArticlesContent>
    </div>

    <div class="page__related">
      <h2
        class="typography__headline2"
        v-html="t('pages.faq.otherArticles')"
      />

      <FaqArticlesList :articles="relatedArticles" />
    </div>
  </section>

  <LazyFaqArticlesImageModal
    v-if="isModalOpen(MODAL.FaqArticlesImage) && activeImageSource"
    :src="activeImageSource"
    @close="() => (activeImageSource = null)"
  />
</template>

<script lang="ts" setup>
const { t } = useI18n();
const { isModalOpen } = useModalStore();
const activeImageSource = ref<null | string>(null);

const breadcrumbs = [
  {
    name: t("pages.faq.title"),
    url: "/faq",
  },
  {
    name: "Заявки на&nbsp;услуги",
    url: "/faq/categories/zayavki-na-uslugi",
  },
  {
    name: "Добавление и&nbsp;редактирование заявки",
  },
];

const menu = [
  {
    name: "Как&nbsp;попасть",
    url: "kak-popast",
  },
  {
    name: "Добавление&nbsp;заявки",
    url: "dobavleniye-zayavki",
  },
  {
    name: "Редактирование&nbsp;заявки",
    url: "redaktirovaniye-zayavki",
  },
];

const relatedArticles = [
  {
    name: "Что такое заявки на услуги",
    slug: "chto-takoye-zayavki-na-uslugi",
  },
  {
    name: "Список заявок и&nbsp;их данные",
    slug: "spisok-zayavok-i-ikh-dannye",
  },
];

useSeoMeta({
  title: "Добавление и редактирование заявки",
});
</script>

<style scoped>
.page {
  display: flex;
  flex-direction: column;
  row-gap: 40px;
}

.page__header {
  display: flex;
  flex-direction: column;
  row-gap: 40px;
}

.page__title {
  display: flex;
  flex-direction: column;
  row-gap: 24px;
}

.page__content {
  display: flex;
  flex-direction: column;
  gap: 40px;
}

@media (--xl) {
  .page__content {
    flex-direction: row-reverse;
    justify-content: start;
    align-items: start;
  }
}

.page__related {
  display: flex;
  flex-direction: column;
  row-gap: 24px;
}

p a {
  display: contents;
}
</style>
